import { useCallback, useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { Asset, ASSET_TYPE, Broadcast, QUERY_KEYS } from '@24i/nxg-sdk-photon/src';
import { useContentData } from '@24i/nxg-sdk-smartott-shared/src/context/ContentData';
import { getContinueWatchingOffset } from '../continueWatching/useContinueWatchingQuery';
import { useFetchedAsset } from './useFetchedAssets';

const useAssetDetailsQuery = (
    {
        assetId,
        assetType,
        continueWatchingPlaylist,
        channelId,
        sectionLabel,
    }: {
        assetId?: string;
        assetType?: ASSET_TYPE;
        continueWatchingPlaylist?: Asset[];
        channelId?: string | null;
        sectionLabel?: string | null;
    },
    options?: UseQueryOptions<Asset | Broadcast>
) => {
    const isOptionEnabled = options?.enabled !== undefined ? options.enabled : true;
    const { fetchAsset } = useContentData();
    const localAsset: Asset | Broadcast | undefined = useFetchedAsset(assetId);
    const continueWatchingOffset = useMemo(
        () =>
            continueWatchingPlaylist && assetId
                ? getContinueWatchingOffset(continueWatchingPlaylist, assetId)
                : undefined,
        [continueWatchingPlaylist]
    );

    const fetchDetails = async (): Promise<Asset | Broadcast> => {
        if (!assetId || !assetType)
            throw Error('Asset id or type are not provided and it is required to fetch details.');

        try {
            return await fetchAsset({
                id: assetId,
                type: assetType,
                channelId,
            });
        } catch (err) {
            throw Error(JSON.stringify(err)); // TODO: use typed Error
        }
    };

    const enhanceDetails = useCallback(
        (fetchedAsset: Asset | Broadcast): Asset | Broadcast => ({
            ...fetchedAsset,
            ...continueWatchingOffset,
            sectionLabel,
        }),
        [continueWatchingOffset, sectionLabel]
    );

    return useQuery([QUERY_KEYS.assets, QUERY_KEYS.assetDetail, assetId], fetchDetails, {
        ...(options as any),
        initialData: localAsset,
        enabled: !!assetId && !!assetType && isOptionEnabled,
        select: enhanceDetails,
    });
};

export default useAssetDetailsQuery;
